import { HTMLAttributes, PropsWithChildren, useState } from 'react';
import {
  FieldValues,
  Path,
  RegisterOptions,
  UseFormReturn,
  useController
} from 'react-hook-form';
import FormField from '../../chat/FormField';
import Input from '../../chat/Input';
import SubLabel from '../../chat/SubLabel';
import { commonPasswordError, passworPattern } from '../../../constants';

export type PasswordProps<T extends FieldValues> = PropsWithChildren<
  {
    displayName?: string;
    passwordChange?: boolean;
    name?: keyof T;
    form: UseFormReturn<T>;
    customValidation?: RegisterOptions<T>;
    showRequiredLabel?: Parameters<typeof FormField>[0]['showRequiredLabel'];
    displayLarge?: Parameters<typeof FormField>[0]['displayLarge'];
  } & HTMLAttributes<HTMLDivElement>
>;
const PasswordInput = <T extends FieldValues>({
  children,
  name = 'password',
  displayName = 'パスワード',
  passwordChange,
  form,
  customValidation,
  showRequiredLabel,
  displayLarge,
  ...props
}: PasswordProps<T>) => {
  const {
    control,
    formState: { errors }
  } = form;

  const {
    field,
    fieldState: { invalid, isTouched, isDirty },
    formState: { touchedFields, dirtyFields }
  } = useController({
    name: name as Path<T>,
    control,
    rules: {
      required: { value: true, message: `${displayName}は必須` },
      ...(customValidation || {})
    }
  });

  return (
    <>
      <FormField
        label={displayName}
        subLabel={[
          !!passwordChange && (
            <SubLabel key={`${name as string}-label`}>
              ※パスワードを変更しない場合は空欄のままで結構です
            </SubLabel>
          ),
          children
        ]}
        field={Input}
        fieldProps={{
          type: 'password',
          passwordVisibilityTogglable: true
        }}
        value={field.value}
        onChange={field.onChange}
        rhfRef={field.ref}
        error={!!errors?.[name]?.message}
        errorText={errors?.[name]?.message?.toString()}
        displayLarge={displayLarge}
        showRequiredLabel={showRequiredLabel}
      />
    </>
  );
};

export const DefaultPasswordInput = <T extends FieldValues>(
  props: Omit<PasswordProps<T>, 'customValidation'>
) => {
  return (
    <PasswordInput
      {...props}
      customValidation={{
        pattern: {
          value: passworPattern,
          message: commonPasswordError
        }
      }}
    />
  );
};

export default PasswordInput;
